import React, { ChangeEvent, useEffect, useState } from 'react';
import Alert from 'Components/Alert';
import FormGroup from 'Components/Form/FormGroup';
import FormLabel from 'Components/Form/FormLabel';
import SelectInput from 'Components/Form/SelectInput';
import TextInput from 'Components/Form/TextInput';
import Button from 'Components/Link/Button';
import Modal from 'Components/Modal/Modal';
import ModalBody from 'Components/Modal/ModalBody';
import ModalContent from 'Components/Modal/ModalContent';
import ModalFooter from 'Components/Modal/ModalFooter';
import ModalHeader from 'Components/Modal/ModalHeader';
import { kinds } from 'Helpers/Props';
import { getTdarrAccessToken } from '../api';
import { TdarrLibrary } from '../types';

interface UploadFilesModalProps {
  isOpen: boolean;
  libraries: TdarrLibrary[];
  onClose: () => void;
  onUploaded: () => void;
}

export default function UploadFilesModal({ isOpen, libraries, onClose, onUploaded }: UploadFilesModalProps) {
  const [libraryId, setLibraryId] = useState('');
  const [destination, setDestination] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ kind: 'success' | 'danger'; text: string } | null>(null);
  const selectedLibrary = libraries.find((library) => library._id === libraryId);

  useEffect(() => {
    if (!isOpen) return;
    const initial = libraries[0];
    setLibraryId(initial?._id ?? '');
    setDestination(initial?.folder ?? '');
    setFiles([]);
    setMessage(null);
  }, [isOpen, libraries]);

  const selectLibrary = (value: string) => {
    const library = libraries.find((candidate) => candidate._id === value);
    setLibraryId(value);
    setDestination(library?.folder ?? '');
  };

  const upload = async () => {
    if (!destination.trim() || !files.length) return;
    setUploading(true);
    setMessage(null);
    try {
      for (const file of files) {
        const data = new FormData();
        data.append('filePath', `${destination.replace(/[\\/]$/, '')}/${file.name}`);
        data.append('fileSize', String(file.size));
        data.append('nodeID', '');
        data.append('workerID', '');
        data.append('reqID', '');
        data.append('file', file);
        const token = getTdarrAccessToken();
        const response = await fetch(`${window.Sonarr.urlBase}/api/v2/file/upload`, {
          method: 'POST', body: data, headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });
        if (!response.ok) throw new Error(`${file.name}: upload failed (${response.status})`);
      }
      setMessage({ kind: 'success', text: `${files.length} file${files.length === 1 ? '' : 's'} uploaded.` });
      setFiles([]);
      onUploaded();
    } catch (error) {
      setMessage({ kind: 'danger', text: error instanceof Error ? error.message : 'Upload failed.' });
    } finally {
      setUploading(false);
    }
  };

  return <Modal isOpen={isOpen} onModalClose={onClose}>
    <ModalContent onModalClose={onClose}>
      <ModalHeader>Upload Files</ModalHeader>
      <ModalBody>
        {!libraries.length && <Alert kind={kinds.WARNING}>Create a library before uploading files.</Alert>}
        {message && <Alert kind={message.kind}>{message.text}</Alert>}
        <FormGroup><FormLabel name="uploadLibrary">Library</FormLabel><SelectInput<string> name="uploadLibrary" value={libraryId} values={libraries.map((library) => ({ key: library._id, value: library.name }))} isDisabled={!libraries.length || uploading} onChange={({ value }) => selectLibrary(String(value))} /></FormGroup>
        <FormGroup><FormLabel name="uploadDestination">Destination</FormLabel><TextInput<string> name="uploadDestination" value={destination} placeholder={selectedLibrary?.folder ?? '/media'} readOnly={uploading} onChange={({ value }) => setDestination(String(value))} /></FormGroup>
        <FormGroup><FormLabel name="uploadFiles">Files</FormLabel><div><input id="uploadFiles" name="uploadFiles" type="file" multiple disabled={uploading} onChange={(event: ChangeEvent<HTMLInputElement>) => setFiles(Array.from(event.target.files ?? []))} />{files.length > 0 && <div className="tdarr-upload-summary">{files.length} file{files.length === 1 ? '' : 's'} selected</div>}</div></FormGroup>
      </ModalBody>
      <ModalFooter><Button onPress={onClose}>Close</Button>{' '}<Button kind={kinds.PRIMARY} isDisabled={!files.length || !destination.trim() || uploading} onPress={() => void upload()}>{uploading ? 'Uploading…' : 'Upload'}</Button></ModalFooter>
    </ModalContent>
  </Modal>;
}
